import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { URL_MARK_NOTIFICATION_AS_READ } from '@/notifications/lib/notification-constants';
import { NOTIFICATIONS_LIST_KEY } from './use-user-notifications';

/**
 * Mark a set of notification ids as read.
 *
 *   POST /api/Notifications/MarkNotificationAsRead
 *   body: BARE JSON array `["id1","id2"]` (NOT wrapped object)
 *
 * Mirrors Flutter `notification_center_data_source_impl.dart:38-48` which
 * sends `jsonEncode(notificationId)` directly — the wire body is a
 * top-level array, not `{notificationIds: [...]}`. The api-client's
 * `post<T>(url, body)` accepts arrays as-is via `JSON.stringify(body)`,
 * so this works without special handling. The regression-guard test
 * pins the array-not-object shape.
 *
 * Invalidates `['notifications', 'list']` on success so the panel
 * re-renders with `isRead` flags flipped server-side.
 */

export interface MarkNotificationsAsReadVars {
  notificationIds: string[];
}

export function useMarkNotificationsAsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ notificationIds }: MarkNotificationsAsReadVars) =>
      // Body is the array directly, not wrapped. Flutter datasource:42.
      post<unknown>(URL_MARK_NOTIFICATION_AS_READ, notificationIds),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: NOTIFICATIONS_LIST_KEY });
    },
  });
}
